import { useEffect, useMemo, useRef, useState } from 'react'
import { Store, UserCircle2 } from 'lucide-react'
import { GestorDashboardFiltersPanel } from '@/components/Gestores/GestorDashboardFiltersPanel'
import { GestorDashboardSavedFilters } from '@/components/Gestores/GestorDashboardSavedFilters'
import type { GestorDashboardFilterPreset } from '@/components/Gestores/gestorDashboardPresetTypes'
import {
  FilterBar,
  FilterClearButton,
  FilterPanelButton,
  FilterSearch,
  FilterSelect,
} from '@/components/ui/page-toolbar'

import { filterBarValue } from '@/components/Gestores/gestorDashboardFilterListUtils'

type FilterKey = 'workspaces' | 'executors' | 'taskType' | 'search'

type Option = { value: string; label: string }

type Props = {
  workspaces: string[]
  executors: string[]
  taskType: string
  search: string
  loading?: boolean
  executorOptions: Option[]
  workspaceOptions: Option[]
  taskTypeOptions: Option[]
  presets: GestorDashboardFilterPreset[]
  activePresetId: string | null
  presetsLoading?: boolean
  onChange: (key: FilterKey, value: string | string[]) => void
  onClearAll: () => void
  onApplyPreset: (preset: GestorDashboardFilterPreset) => void
  onCreatePreset: () => void
  onEditPreset: (preset: GestorDashboardFilterPreset) => void
}

export function GestorDashboardFilters({
  workspaces,
  executors,
  taskType,
  search,
  loading = false,
  executorOptions,
  workspaceOptions,
  taskTypeOptions,
  presets,
  activePresetId,
  presetsLoading = false,
  onChange,
  onClearAll,
  onApplyPreset,
  onCreatePreset,
  onEditPreset,
}: Props) {
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [draftTaskType, setDraftTaskType] = useState(taskType)
  const filtersRootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!filtersOpen) return
    setDraftTaskType(taskType)
  }, [filtersOpen, taskType])

  const advancedFilterCount = taskType ? 1 : 0

  const hasActiveFilter = useMemo(
    () =>
      search.trim().length > 0 ||
      workspaces.length > 0 ||
      executors.length > 0 ||
      Boolean(taskType) ||
      Boolean(activePresetId),
    [activePresetId, executors.length, search, taskType, workspaces.length],
  )

  useEffect(() => {
    if (!filtersOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFiltersOpen(false)
    }

    const onPointerDown = (event: MouseEvent) => {
      if (filtersRootRef.current && !filtersRootRef.current.contains(event.target as Node)) {
        setFiltersOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [filtersOpen])

  const applyAdvanced = () => {
    onChange('taskType', draftTaskType)
    setFiltersOpen(false)
  }

  return (
    <FilterBar>
      <GestorDashboardSavedFilters
        presets={presets}
        activePresetId={activePresetId}
        presetsLoading={presetsLoading}
        onSelect={onApplyPreset}
        onCreate={onCreatePreset}
        onEdit={onEditPreset}
      />

      <FilterSearch
        value={search}
        onChange={(value) => onChange('search', value)}
        placeholder='Tarefa, comentário, ticket…'
        ariaLabel='Buscar no dashboard'
      />

      <FilterSelect
        value={filterBarValue(workspaces)}
        onChange={(value) => onChange('workspaces', value ? [value] : [])}
        icon={<Store size={15} className='text-text-muted' aria-hidden />}
        title='Cliente (workspace)'
        ariaLabel='Filtrar por cliente'
      >
        <option value=''>Todos os clientes</option>
        {workspaces.length > 1 && (
          <option value='__multi__' disabled>
            {workspaces.length} clientes selecionados
          </option>
        )}
        {workspaceOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </FilterSelect>

      <FilterSelect
        value={filterBarValue(executors)}
        onChange={(value) => onChange('executors', value ? [value] : [])}
        icon={<UserCircle2 size={15} className='text-text-muted' aria-hidden />}
        title='Executor'
        ariaLabel='Filtrar por executor'
      >
        <option value=''>Todos os executores</option>
        {executors.length > 1 && (
          <option value='__multi__' disabled>
            {executors.length} executores selecionados
          </option>
        )}
        {executorOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </FilterSelect>

      <div ref={filtersRootRef} className='relative shrink-0'>
        <FilterPanelButton
          active={filtersOpen}
          badgeCount={advancedFilterCount}
          expanded={filtersOpen}
          onClick={() => setFiltersOpen((open) => !open)}
        />

        <GestorDashboardFiltersPanel
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onClearAll={() => {
            onClearAll()
            setFiltersOpen(false)
          }}
          taskType={draftTaskType}
          taskTypeOptions={taskTypeOptions}
          onTaskTypeChange={setDraftTaskType}
          onApply={applyAdvanced}
          loading={loading}
        />
      </div>

      {hasActiveFilter && <FilterClearButton onClick={onClearAll} />}
    </FilterBar>
  )
}
